import React, { useEffect, useState } from "react";
import styles from './ProfileUpdateForm.module.scss';
import TextField from "../textField/TextField";
import PropTypes from "prop-types";
import AutoComplete from "../autoComplete/AutoComplete";
import Button from "../button/Button";
import Alert from "../alert/Alert";
import ImageUploader from "../imageUploader/ImageUploader";
import { getInitials } from "../../utils/stringUtils";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import { useAuth } from "../../contexts/AuthContext";
import CustomDatePicker from "../customDatePicker/CustomDatePicker";
import PreferenceSection from "../preferenceSection/PreferenceSection";

const ProfileUpdateForm = ({
  formData,
  healthSystemList = [],
  onHealthSystemChange,
  clinicalSiteList = [],
  specialityList = [],
  onSubmit,
  error,
  success,
  program = null,
}) => {
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [healthSystemIds, setHealthSystemIds] = useState([]);
  const [healthSystemIdsError, setHealthSystemIdsError] = useState('');
  const [clinicalSiteIds, setClinicalSiteIds] = useState([]);
  const [clinicalSiteIdsError, setClinicalSiteIdsError] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [specialityError, setSpecialityError] = useState('');
  const [clinicalSitePreferences, setClinicalSitePreferences] = useState([]);
  const [specialityPreferences, setSpecialityPreferences] = useState([]);
  const [profilePicture, setProfilePicture] = useState({
    initials: '',
    image: null
  });
  const [imageFile, setImageFile] = useState(null);
  const { role } = useAuth();

  const formatPhone = (value) => {
    return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
  };

  useEffect(() => {
    setFirstName(formData?.firstName || '');
    setLastName(formData?.lastName || '');
    setEmail(formData?.email || '');
    setPhone(formData?.mobileNumber ? formatPhone(formData.mobileNumber) : '');
    setHealthSystemIds(formData.healthSystemIds || []);
    setClinicalSiteIds(formData.clinicalSiteIds || []);
    setSpeciality(formData.speciality || '');
    setClinicalSitePreferences(formData.clinicalSitePreferences || []);
    setSpecialityPreferences(formData.specialityPreferences || []);
    setProfilePicture({
      initials: getInitials(formData?.firstName, formData?.lastName),
      image: formData?.profilePicture || null
    });
  }, [formData]);

  const validateFirstName = () => {
    if(!firstName) return 'First Name is required';
    if(!/^[A-Za-z]{2}/.test(firstName)) return 'First Name must contain only letters and be at least 2 characters long.';
    return '';
  };

  const validateLastName = () => {
    if(!lastName) return 'Last Name is required';
    if(!/^[A-Za-z]{2}/.test(lastName)) return 'Last Name must contain only letters and be at least 2 characters long.';
    return '';
  };

  const validateHealthSystemIds = () => {
    if(healthSystemIds.length === 0) return 'Health System is required';
    return '';
  };

  const validateClinicalSiteIds = () => {
    if(clinicalSiteIds.length === 0) return 'Clinical Site is required';
    return '';
  };

  const validateSpeciality = () => {
    if(!speciality) return 'Specialty is required';
    return '';
  };

  const hanldeUpdate = () => {
    const firstNameValidationResult = validateFirstName();
    const lastNameValidationResult = validateLastName();
    const healthSystemIdsValidationResult = role === 'Instructor' ? validateHealthSystemIds() : '';
    const clinicalSiteIdsValidationResult = role === 'Instructor' ? validateClinicalSiteIds() : '';
    const specialityValidationResult = role === 'Instructor' ? validateSpeciality() : '';

    setFirstNameError(firstNameValidationResult);
    setLastNameError(lastNameValidationResult);
    if(role === 'Instructor') {
      setHealthSystemIdsError(healthSystemIdsValidationResult);
      setClinicalSiteIdsError(clinicalSiteIdsValidationResult);
      setSpecialityError(specialityValidationResult);
    }

    if (firstNameValidationResult || lastNameValidationResult ||
        healthSystemIdsValidationResult || clinicalSiteIdsValidationResult ||
        specialityValidationResult) {
      return false;
    }

    let data = { firstName, lastName };

    if(role === 'Instructor') {
      data = { ...data, healthSystemIds, clinicalSiteIds, speciality };
    }

    if(role === 'Student') {
      data = {
        ...data,
        clinicalSitePreferences: clinicalSitePreferences.map((item, idx) => ({ id: item.id, order: idx + 1 })),
        specialityPreferences: specialityPreferences.map((item, idx) => ({ id: item.id, order: idx + 1 })),
      };
    }

    onSubmit(data, imageFile);
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        hanldeUpdate();
      }}
    >
      { error && <Alert message={error} type="error" /> }
      { success && <Alert message={success} type="success" />}
      <ImageUploader
        {...profilePicture}
        onChange={(value) => setImageFile(value)}
      />
      <div className={styles.row}>
        <div className={styles.column}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={(value) => setFirstName(value)}
            error={firstNameError}
          />
        </div>
        <div className={styles.column}>
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(value) => setLastName(value)}
            error={lastNameError}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(value) => setEmail(value)}
            disabled={true}
          />
        </div>
        <div className={styles.column}>
          <TextField
            label="Phone"
            value={phone}
            onChange={(value) => setPhone(value)}
            disabled={true}
          />
        </div>
      </div>
      {role === 'Instructor' && <>
        <div className={`${styles.row} ${styles.vertical}`}>
          <div className={styles.column}>
            <MultiSelectDropdown
              label="Health System"
              options={healthSystemList}
              selected={healthSystemIds}
              onChange={(selected) => {
                setHealthSystemIds(selected);
                onHealthSystemChange(selected);
              }}
              error={healthSystemIdsError}
            />
          </div>
          <div className={styles.column}>
            <MultiSelectDropdown
              label="Clinical Site"
              options={clinicalSiteList}
              selected={clinicalSiteIds}
              onChange={(selected) => setClinicalSiteIds(selected)}
              error={clinicalSiteIdsError}
            />
          </div>
          <div className={styles.column}>
            <AutoComplete
              label="Specialty"
              value={speciality}
              onChange={(value) => setSpeciality(value)}
              error={specialityError}
              list={specialityList.map(item => item.label)}
            />
          </div>
        </div>
      </>}
      {role === 'Student' && program && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Program Details</h2>
          <div className={styles.sectionRow}>
            <div className={styles.col}>
              <TextField label="Program Name" value={program.title ?? ''} disabled onChange={() => {}} />
            </div>
            <div className={styles.col}>
              <TextField label="Status" value={program.status === true ? 'Active' : program.status === false ? 'Inactive' : ''} disabled onChange={() => {}} />
            </div>
          </div>
          <div className={styles.sectionRow}>
            <div className={styles.col25}>
              <CustomDatePicker label="Start Date" date={program.startDate ? new Date(program.startDate) : null} onChange={() => {}} disabled />
            </div>
            <div className={styles.col25}>
              <CustomDatePicker label="End Date" date={program.endDate ? new Date(program.endDate) : null} onChange={() => {}} disabled />
            </div>
            <div className={styles.col50}>
              <TextField label="Current Year" value={program.currentYear?.year ?? ''} disabled onChange={() => {}} />
            </div>
          </div>
        </div>
      )}
      {role === 'Student' && (
        <>
          <PreferenceSection
            title="Clinical Site Preferences"
            hint="Add your preferred clinical sites and below, then drag or use arrows to rank them in order of preference."
            options={clinicalSiteList}
            items={clinicalSitePreferences}
            onChange={setClinicalSitePreferences}
          />
          <PreferenceSection
            title="Specialty Preferences"
            hint="Add your preferred speciality and below, then drag or use arrows to rank them in order of preference."
            options={specialityList}
            items={specialityPreferences}
            onChange={setSpecialityPreferences}
          />
        </>
      )}
      <div className={styles.submit}>
        <Button
          label="Update"
          type="submit"
          size="small"
        />
      </div>
    </form>
  );
};

ProfileUpdateForm.propTypes = {
  formData: PropTypes.object,
  healthSystemList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onHealthSystemChange: PropTypes.func.isRequired,
  clinicalSiteList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  specialityList: PropTypes.arrayOf(PropTypes.any).isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  success: PropTypes.string,
  program: PropTypes.object,
};

export default ProfileUpdateForm;
